import { AdminUsersAction } from "@/constants/admin-accounts";
import { ModalWidth } from "@/constants/modals";
import AddAdminUserModal from "@/components/modals/AddAdminUserModal.vue";
import type { AdminUser } from "@/_store/admin-users.module";
import {
  defaultDialogsConfig,
  type DialogConfig,
  type DialogDataConfig,
  useDialogsStore,
} from "@/_store/dialogs.module";
import { defineAsyncComponent } from "vue";
import {
  AccessPermissionAction,
  AccessPermissionLocationPanel,
  AccessPermissionType,
  Service,
  ServiceAction,
  ServiceStatus,
} from "@/constants/cloud-apps";
import type { LocationQuery } from "vue-router";
import AddAccessPermissionModal from "@/components/modals/AddAccessPermissionModal.vue";
import { useAccessPermissionsStore } from "@/_store/cloud-apps/access-permissions.module.ts";
import {
  AgentDeploymentAction,
  DevicePostureAction,
  DevicePostureOsType,
  type DevicePostureSettings,
  LabelAction,
} from "@/constants/devices";
import AddLabelModal from "@/components/modals/AddLabelModal.vue";
import type { DeviceVersionItem } from "@/_store/devices-settings.module";
import { type DeviceLabel, useDevicesSettingsStore } from "@/_store/devices-settings.module";
import AddOrEditPostureModal from "@/components/modals/device-posture/AddOrEditPostureModal.vue";
import { useDevicesPostureStore } from "@/_store/device-posture/devices-posture-settings.module";
import { GlobalPolicySection } from "@/constants/msp.ts";
import SetIndustryModal from "@/components/modals/data-governance/SetIndustryModal.vue";
import { usePrivacySensitiveDataStore } from "@/_store/endpoint-data-governance/privacy-sensitive-data.module";
import { EndpointDataGovernanceAction } from "@/constants/endpoint-data-governance";
import { useUserDataGovernanceModule } from "@/_store/user-data-governance/user-data-governance.module";
import { UserDataGovernanceAction } from "@/constants/user-data-governance";
import type { ConnectAppModalConfigItem } from "@/components/modals/cloud-apps/cloud-apps-modal.types";
import { useCloudAppsStore } from "@/_store/cloud-apps/cloud-apps.module";
import { DemoWillStartModal } from "@/constants/tutorial.ts";
import router from "@/_helpers/router.ts";
import { i18n } from "@/plugins/i18n";
import { SubscriptionModule } from "@/constants/workplaces.ts";
import { useSnackbarStore } from "@/_store/snackbar.module";
import CopyLinkModal from "@/components/modals/CopyLinkModal.vue";
import { copyToClipboard } from "@coronet/cui";
import { createDownloadLink, downloadFileByLink } from "@/_helpers/download";
import {
  getDisclaimerText,
  isWorkspaceFrozen,
  isWorkspaceNewAndInDemoMode,
} from "@/_helpers/subscription";
import CloudAppConnectionStatusModal from "@/components/modals/cloud-apps/CloudAppConnectionStatusModal.vue";
import {
  canOverrideGlobalPolicy,
  isActionRestricted,
  isWorkspaceFrozenOrActionRestricted,
} from "@/_helpers/permissions.ts";
import cloneDeep from "lodash/cloneDeep";
import defaultsDeep from "lodash/defaultsDeep";
import type { ComponentDialogConfig, ConfirmationDialogConfig, ListDialogConfig } from "@/types.ts";
import ListModal from "@/components/modals/network/ListModal.vue";
import ExportTelemetryModal from "@/components/modals/exports/ExportTelemetryModal.vue";
import {
  type EdrTelemetryExportFilters,
  type ExportDialogPayload,
  useExportsStore,
} from "@/_store/exports.module";
import { ExportAction, ExportChoice } from "@/constants/exports";
import { RolePermissionsScope, WorkspaceManagementScopeSections } from "@/constants/roles.ts";

const ConnectServiceModal = defineAsyncComponent(
  () => import("@/views/settings/cloud-apps/modals/ConnectServiceModal.vue")
);

/**
 * Generic dialogs config constructor, use it when custom dialog config should be created
 * @param config
 */
export const dialogsConfigConstructor = (config: Partial<DialogConfig>): DialogConfig => {
  const defaultState = cloneDeep(defaultDialogsConfig);
  return defaultsDeep(config, defaultState);
};
/**
 * Dialogs config constructor for confirmation dialogs, covers most of the cases of confirmation dialogs
 * @param item
 * @param action
 * @param callback
 * @param disable
 * @param disclaimer
 * @param width
 * @param text
 * @param title
 */
export const confirmationDialogsConfigConstructor = ({
  item,
  action,
  callback,
  disable,
  disclaimer,
  width,
  text,
  title,
}: ConfirmationDialogConfig): DialogConfig => {
  const dialogConfig = {
    width: width || ModalWidth.SMALL,
    header: {
      title: title || i18n.global.t(`modals.${action}.title`, { ...item }),
      close: true,
    },
    content: {
      html: text || i18n.global.t(`modals.${action}.description`, { ...item }),
    },
    disable: disable ?? isWorkspaceFrozen(),
    disclaimer: getDisclaimerText(disable, disclaimer),
    footer: {
      buttons: [
        {
          title: i18n.global.te(`modals.${action}.cancelBtn`)
            ? i18n.global.t(`modals.${action}.cancelBtn`)
            : i18n.global.t("general.cancel"),
          spacer: "before",
          type: "text",
          cancel: true,
        },
        {
          title: i18n.global.t(`modals.${action}.actionBtn`),
          color: "primary",
          callback: callback,
          cancel: false,
        },
      ],
    },
    data: {
      item,
      action,
    },
  };

  return dialogsConfigConstructor(dialogConfig);
};
/**
 * Generic constructor for dialogs with custom components
 * @param item
 * @param action
 * @param component
 * @param width
 * @param callback
 * @param closeCallback
 * @param disable
 * @param disclaimer
 * @param hideFooter
 * @param scrollable
 * @param customErrorHandling
 * @param showValidationAlert
 * @param title
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- mirrors DialogConfig<T> from dialogs.module to support heterogeneous dialog item passthrough
export const componentDialogsConfigConstructor = <T = any>({
  item,
  action,
  component,
  width,
  callback,
  closeCallback,
  disable,
  disclaimer,
  hideFooter,
  scrollable,
  customErrorHandling,
  showValidationAlert,
  title,
}: ComponentDialogConfig<T>): DialogConfig => {
  const dialogConfig: DialogConfig = {
    width,
    header: {
      title: title || i18n.global.t(`modals.${action}.title`, { ...item }),
      close: true,
    },
    content: { component },
    disable: disable ?? isWorkspaceFrozen(),
    footer: {
      buttons: [
        {
          title: i18n.global.te(`modals.${action}.cancelBtn`)
            ? i18n.global.t(`modals.${action}.cancelBtn`)
            : i18n.global.t("general.cancel"),
          spacer: "before",
          type: "text",
          cancel: true,
        },
        {
          title: i18n.global.t(`modals.${action}.actionBtn`),
          color: "primary",
          callback,
          cancel: false,
        },
      ],
    },
    disclaimer: getDisclaimerText(disable, disclaimer),
    data: {
      item,
      action,
      callback,
    },
    closeCallback,
    scrollable,
    customErrorHandling,
    showValidationAlert,
  };

  if (hideFooter) {
    dialogConfig.footer = null;
  }

  return dialogsConfigConstructor(dialogConfig);
};
/**
 * Dialog config constructor for list-style modals rendered via ListModal.
 * @param item
 * @param action
 * @param width
 */
export const listDialogConstructor = <T>({
  item,
  action,
  width,
}: ListDialogConfig<{ items: T[] }>): DialogConfig => {
  const dialogConfig: DialogConfig = {
    width: width ?? ModalWidth.MEDIUM,
    disable: false,
    disclaimer: "",
    header: {
      title: i18n.global.t(`modals.${action}.title`, { ...item, n: item?.items.length }),
      close: true,
    },
    content: {
      component: ListModal,
    },
    data: {
      item,
      action,
    },
    scrollable: true,
    footer: null,
  };
  return dialogsConfigConstructor(dialogConfig);
};
/**
 * Opens the demo-mode restriction warning modal for the given action context.
 * @param action
 */
export const openDemoModeRestrictionModal = (action: DemoWillStartModal) => {
  const dialogsStore = useDialogsStore();

  dialogsStore.openDialog(
    dialogsConfigConstructor({
      width: ModalWidth.SMALL,
      header: {
        title: i18n.global.t(`modals.${action}.title`),
        titleClass: "text-red-dark",
        titleIcon: "warning",
      },
      content: {
        html: i18n.global.t(`modals.${action}.description`),
      },
      footer: {
        buttons: [
          {
            title: i18n.global.t(`modals.${action}.actionBtn`),
            color: "primary",
            spacer: "before",
            callback: dialogsStore.closeDialog,
            cancel: false,
          },
        ],
      },
    })
  );
};
/**
 * Builds a LARGE-width `AddAdminUserModal` dialog wired to the `AdminUsersAction.ADD` action,
 * disabling the submit button when the user lacks the `add` permission on the
 * `WORKSPACE_MANAGEMENT.ADMIN_USERS` scope.
 * @param addAdminUserCallback
 */
export const getAddAdminUserDialogConfig = (
  addAdminUserCallback: (data: DialogDataConfig<AdminUser>) => Promise<void>
): DialogConfig => {
  return componentDialogsConfigConstructor({
    action: AdminUsersAction.ADD,
    component: AddAdminUserModal,
    width: ModalWidth.LARGE,
    callback: addAdminUserCallback,
    disable: isActionRestricted(
      RolePermissionsScope.WORKSPACE_MANAGEMENT,
      WorkspaceManagementScopeSections.ADMIN_USERS,
      "add"
    ),
  });
};

/**
 * Builds the dialog config for the telemetry CSV export modal
 * @param disable
 */
export const getExportTelemetryDialogConfig = (
  disable: boolean = isWorkspaceFrozenOrActionRestricted(
    RolePermissionsScope.WORKSPACE_MANAGEMENT,
    WorkspaceManagementScopeSections.REPORTS
  )
): DialogConfig => {
  const exportsStore = useExportsStore();
  const snackbarStore = useSnackbarStore();

  return {
    ...componentDialogsConfigConstructor({
      action: ExportAction.EXPORT_TELEMETRY,
      component: ExportTelemetryModal,
      width: ModalWidth.LARGE,
      disable,
      callback: async (data: ExportDialogPayload<EdrTelemetryExportFilters>) => {
        await exportsStore.exportTelemetryToCsv({
          types: data.type === ExportChoice.SPECIFIC ? data.filters.types : [],
          timeRange: data.filters.timeRange,
        });
        await exportsStore.getExportsList();
        snackbarStore.addGenericSuccess(
          i18n.global.t("snackbar.messages.exports.exportCsvStarted")
        );
      },
    }),
    disablePersistentAnimation: true,
  };
};

export const openConnectCloudAppsModal = (
  action: ServiceAction,
  item: Partial<ConnectAppModalConfigItem>
) => {
  const dialogsStore = useDialogsStore();
  const cloudAppsStore = useCloudAppsStore();
  const actionNotAllowed = isWorkspaceFrozenOrActionRestricted(
    RolePermissionsScope.WORKSPACE_MANAGEMENT,
    WorkspaceManagementScopeSections.CLOUD_APPS
  );

  switch (action) {
    case ServiceAction.GRANT_PRIVILEGES:
    case ServiceAction.CONNECT: {
      const baseConfig = componentDialogsConfigConstructor({
        width: ModalWidth.LARGE,
        action,
        item: { ...item, partialScan: true },
        component: ConnectServiceModal,
        disable: actionNotAllowed,
        callback: () => {},
        closeCallback: () => router.replace({ query: {} }),
      });

      const dialogExtras = {
        header: {
          title: i18n.global.t(`modals.${ServiceAction.CONNECT}.noServiceTitle`),
          close: true,
        },
        footer: null,
        scrollable: true,
      };

      const dialogConfig = { ...baseConfig, ...dialogExtras };

      if (action === ServiceAction.CONNECT && isWorkspaceNewAndInDemoMode()) {
        return openDemoModeRestrictionModal(DemoWillStartModal.CLOUD_APPS);
      }

      dialogsStore.openDialog(dialogConfig);
      break;
    }
    case ServiceAction.REMOVE:
    case ServiceAction.DISCONNECT: {
      dialogsStore.openDialog({
        ...confirmationDialogsConfigConstructor({
          action: ServiceAction.DISCONNECT,
          text: i18n.global.t(`modals.${ServiceAction.DISCONNECT}.description`, {
            service: i18n.global.t(`services.${item.name}`),
          }),
          callback: () => cloudAppsStore.disconnect(item.name!),
          disable: isActionRestricted(
            RolePermissionsScope.WORKSPACE_MANAGEMENT,
            WorkspaceManagementScopeSections.CLOUD_APPS
          ),
        }),
      });
      break;
    }
    case ServiceAction.CONNECTION_STATUS: {
      dialogsStore.openDialog(
        componentDialogsConfigConstructor({
          width: ModalWidth.LARGE,
          action: ServiceAction.CONNECTION_STATUS,
          item,
          component: CloudAppConnectionStatusModal,
          disable: false,
          callback: () => {},
          hideFooter: true,
        })
      );
      break;
    }
  }
};

/**
 * Builds the dialog config for adding cloud apps access permissions.
 * @param item
 * @param disable
 */
export const getAddAccessPermissionsDialogConfig = (
  item: {
    action: AccessPermissionAction;
    userType: AccessPermissionType;
    locationPanel?: AccessPermissionLocationPanel;
  } = {
    action: AccessPermissionAction.ADD,
    userType: AccessPermissionType.ALL_USERS,
    locationPanel: AccessPermissionLocationPanel.PERMITTED,
  },
  disable: boolean = isWorkspaceFrozenOrActionRestricted(
    RolePermissionsScope.PROTECTION,
    SubscriptionModule.CLOUD_SECURITY
  )
) => {
  return componentDialogsConfigConstructor({
    action: item.action,
    item: {
      userType: item.userType,
      locationPanel: item.locationPanel ?? AccessPermissionLocationPanel.PERMITTED,
    },
    component: AddAccessPermissionModal,
    scrollable: true,
    callback: useAccessPermissionsStore().addAccessPermissions,
    width: ModalWidth.LARGE,
    disable,
  });
};

/**
 * Builds the dialog config for creating or editing a device label.
 * @param action
 * @param item
 * @param disable
 */
export const getAddLabelDialogConfig = (
  action: LabelAction.EDIT_LABEL | LabelAction.CREATE_LABEL,
  item?: DeviceLabel,
  disable: boolean = isWorkspaceFrozenOrActionRestricted(
    RolePermissionsScope.WORKSPACE_MANAGEMENT,
    WorkspaceManagementScopeSections.DEVICES
  )
): DialogConfig => {
  const devicesSettingsStore = useDevicesSettingsStore();
  return componentDialogsConfigConstructor({
    component: AddLabelModal,
    item,
    action,
    width: ModalWidth.X_SMALL,
    callback:
      action === LabelAction.CREATE_LABEL
        ? devicesSettingsStore.createLabel
        : devicesSettingsStore.editLabel,
    disable,
  });
};

/**
 * Builds the dialog config for adding or editing a device posture policy.
 * @param params
 * @param labels
 * @param policyType
 * @param disable
 */
export const getAddDevicePostureDialogConfig = (
  {
    osType,
    action,
  }: {
    osType: DevicePostureOsType;
    action: DevicePostureAction;
  },
  labels: DeviceLabel[] = [],
  policyType?: DevicePostureSettings,
  disable: boolean = isWorkspaceFrozenOrActionRestricted(
    RolePermissionsScope.PROTECTION,
    SubscriptionModule.ENDPOINT_SECURITY
  )
): DialogConfig => {
  const devicesPostureModule = useDevicesPostureStore();
  const isEditRestricted = !canOverrideGlobalPolicy(GlobalPolicySection.ENDPOINT_SECURITY);

  const config = componentDialogsConfigConstructor({
    action,
    item: {
      osType,
      labels,
      policyType,
    },
    width: ModalWidth.LARGE,
    component: AddOrEditPostureModal,
    callback: devicesPostureModule.addDevicePosturePolicy,
    disable: isEditRestricted || disable,
    scrollable: true,
  });

  const translatedOsType = i18n.global.t(`devicesSettings.devicePostureTab.osTypes.${osType}`);
  if (isEditRestricted) {
    config.disclaimer = i18n.global.t("general.settingsConfiguredByAdmin");
  }

  return {
    ...config,
    header: {
      title: i18n.global.t(`modals.${action}.title`, { osType: translatedOsType }),
    },
  };
};

/**
 * Builds the dialog config for setting the industry for endpoint data governance privacy config.
 * @param item
 * @param disable
 */
export const getSetIndustryDialogConfig = (
  item?: { industries: string[] },
  disable: boolean = isWorkspaceFrozenOrActionRestricted(
    RolePermissionsScope.PROTECTION,
    SubscriptionModule.ENDPOINT_DATA_GOVERNANCE
  )
): DialogConfig => {
  const privacySensitiveDataStore = usePrivacySensitiveDataStore();
  const isEditRestricted = !canOverrideGlobalPolicy(GlobalPolicySection.ENDPOINT_DATA_GOVERNANCE);

  const config = componentDialogsConfigConstructor({
    action: EndpointDataGovernanceAction.SET_INDUSTRY,
    component: SetIndustryModal,
    width: ModalWidth.LARGE,
    disable: isEditRestricted || disable,
    item: item || { industries: [] },
    callback: async (val: { selectedIndustries: string[] }) => {
      await privacySensitiveDataStore.updatePrivacyConfig(val.selectedIndustries);
    },
  });

  if (isEditRestricted) {
    config.disclaimer = i18n.global.t("general.settingsConfiguredByAdmin");
  }

  return config;
};

/**
 * Builds the dialog config for setting the industry for user data governance privacy config.
 * @param item
 * @param disable
 */
export const getSetUserDataIndustryDialogConfig = (
  item?: { industries: string[]; locations?: string[] },
  disable: boolean = isWorkspaceFrozenOrActionRestricted(
    RolePermissionsScope.PROTECTION,
    SubscriptionModule.USER_DATA_GOVERNANCE
  )
): DialogConfig => {
  const userDataStore = useUserDataGovernanceModule();
  const isEditRestricted = !canOverrideGlobalPolicy(GlobalPolicySection.USER_DATA_GOVERNANCE);

  const config = componentDialogsConfigConstructor({
    action: UserDataGovernanceAction.SET_INDUSTRY,
    component: SetIndustryModal,
    width: ModalWidth.LARGE,
    disable: isEditRestricted || disable,
    item: item || { selectedIndustries: [] },
    callback: async (val: { selectedIndustries: string[] }) => {
      await userDataStore.updatePrivacyConfig(val.selectedIndustries);
    },
  });

  if (isEditRestricted) {
    config.disclaimer = i18n.global.t("general.settingsConfiguredByAdmin");
  }

  return config;
};

/**
 * Handles agent deployment actions (copy link, download) and gates on demo-mode restrictions.
 * @param action
 * @param item
 * @param downloadClientPageLink
 */
export async function onAgentDeploymentActionClick(
  action: AgentDeploymentAction,
  item: DeviceVersionItem,
  downloadClientPageLink: string
): Promise<void> {
  const dialogsStore = useDialogsStore();
  const snackbarStore = useSnackbarStore();

  const handleAction = async () => {
    const downloadLink = await createDownloadLink(item, downloadClientPageLink);
    switch (action) {
      case AgentDeploymentAction.COPY_LINK:
        dialogsStore.openDialog(
          componentDialogsConfigConstructor({
            item: { inviteLink: downloadLink },
            action: AdminUsersAction.COPY_INVITE_LINK,
            component: CopyLinkModal,
            width: ModalWidth.LARGE,
            disable: isWorkspaceFrozenOrActionRestricted(
              RolePermissionsScope.WORKSPACE_MANAGEMENT,
              WorkspaceManagementScopeSections.DEVICES
            ),
            callback: () => {
              copyToClipboard(downloadLink);
              snackbarStore.addGenericSuccess(
                i18n.global.t("snackbar.messages.general.copiedToClipboard")
              );
            },
          })
        );
        break;
      case AgentDeploymentAction.DOWNLOAD:
        downloadFileByLink(downloadLink);
        break;
    }
  };

  if (isWorkspaceNewAndInDemoMode()) {
    return openDemoModeRestrictionModal(DemoWillStartModal.DEVICES);
  }

  await handleAction();
}

/**
 * Handles internal cloud apps redirects by opening the connection status modal for the targeted service.
 * @param routeQuery
 */
function handleCloudAppsInternalRedirects(routeQuery: LocationQuery): void {
  if (routeQuery.grantPermissions === "true" && routeQuery.service) {
    openConnectCloudAppsModal(
      ServiceAction.CONNECTION_STATUS,
      useCloudAppsStore().services.find(
        (service) => service.name === (routeQuery.service as Service)
      )!
    );
  }
}

/**
 * Routes cloud-apps deep links to the correct modal based on the route query: `internal=true`
 * opens the connection-status modal for the named `service`; Office 365 with `grantPermissions=true`
 * opens CONNECT; `isNextStep=true` opens GRANT_PRIVILEGES (with `grantPermissions=true`) or CONNECT;
 * `noServicesConnected=true` opens CONNECT and clears the query; Dropbox/Slack/Salesforce open CONNECT
 * with `CONNECTED` status.
 * @param routeQuery
 */
export function processCloudAppsQueryParams(routeQuery: LocationQuery): void {
  if (routeQuery.internal === "true") {
    handleCloudAppsInternalRedirects(routeQuery);
    return;
  }

  if (routeQuery.service === Service.OFFICE_365 && routeQuery.grantPermissions === "true") {
    openConnectCloudAppsModal(ServiceAction.CONNECT, {
      name: routeQuery.service as Service,
      serviceStatus: ServiceStatus.CONNECTED,
    });
    return;
  }

  if (routeQuery.isNextStep === "true" && routeQuery.grantPermissions === "true") {
    openConnectCloudAppsModal(ServiceAction.GRANT_PRIVILEGES, {
      name: routeQuery.service as Service,
    });
    return;
  }

  if (routeQuery.isNextStep === "true") {
    openConnectCloudAppsModal(ServiceAction.CONNECT, { name: routeQuery.service as Service });
    return;
  }

  if (routeQuery.noServicesConnected === "true") {
    openConnectCloudAppsModal(ServiceAction.CONNECT, {
      showOnlyPrimaryCloudApps: routeQuery.showOnlyPrimaryCloudApps === "true",
    });
    router.replace({ query: {} });
    return;
  }

  if (
    [Service.DROPBOX, Service.SLACK, Service.SALES_FORCE].includes(routeQuery.service as Service)
  ) {
    openConnectCloudAppsModal(ServiceAction.CONNECT, {
      name: routeQuery.service as Service,
      serviceStatus: ServiceStatus.CONNECTED,
    });
  }
}
